/**
 * The query-parameter detail routes (#533) — the one definition of what a URL
 * that names a shopping list, a shopping trip, a chore or a project looks like.
 *
 * Some detail views own a path (`/inventory/assets/{id}`, `/recipes/{id}`) and
 * the web shell's own router models those, because it has to pick a tab before
 * any feature mounts. The rest live on their tab's path with the object id in
 * the query string, and those are the ones that kept going wrong: several
 * places decided what such a URL looks like and none of them was in charge.
 *
 * `apps/web/src/app/detailRoutes.ts` made the WEB side one owner. This module
 * exists because that was not the whole set: the API composes the same URLs
 * too — the persisted `routePath` on every semantic-search document, and the
 * `routePath` on an assistant fact — and a parameter name that lives only in
 * the web bundle is a parameter name the server has to guess. It is kept here,
 * with no imports and no browser or node APIs, so both sides can read it.
 *
 * The web module adds the `Tab` each route belongs to, which is a web-only
 * concept; only the path and the parameter name mean anything to the server.
 *
 * A CAUTION for anyone adding a route here: the parameter carries whatever id
 * the WEB payload exposes for that object, which is not always the database
 * row id. `tripId` is a shopping trip's `public_id` (`trip_…`), because that is
 * what `/api/v1/shopping` puts in a trip's `id`. Handing the row uuid to a
 * server-side composer would build a link that looks right and opens nothing.
 */
export const DETAIL_ROUTE_SHAPES = {
    shoppingList: { path: "/shopping", param: "listId" },
    shoppingTrip: { path: "/shopping", param: "tripId" },
    chore: { path: "/chores", param: "taskId" },
    project: { path: "/projects", param: "projectId" }
};
export const detailRouteKeys = Object.keys(DETAIL_ROUTE_SHAPES);
/**
 * The canonical path that opens `id`. This is what a deep link carries and what
 * the view writes when the reader opens the object, so the two cannot drift.
 * Without an id it is the index — the honest landing for a reference that names
 * no particular object, and the degradation these routes have always taken in
 * preference to a link that opens nothing.
 */
export function detailRoutePath(key, id) {
    const { path, param } = DETAIL_ROUTE_SHAPES[key];
    const value = id == null ? "" : String(id).trim();
    if (!value)
        return path;
    const params = new URLSearchParams();
    params.set(param, value);
    return `${path}?${params.toString()}`;
}
